import * as yup from 'yup';
import {v4 as uuid} from 'uuid';

const addFloorSchema = yup.object().shape({
  country: yup.string().required(),
  city: yup.string().required(),
  building: yup.string().required(),
  floorName: yup.string().required(),
  areaSize: yup.number().required().min(1).nullable(),
  maxCapacity: yup.number().required().min(1).max(1000).nullable()
});

const validateFloorInfo = (floor) => {
  try {
    addFloorSchema.validateSync(floor);
    return true;
  } catch (error) {
    return false;
  }
};

const validateFloorArea = ({multipleAreas, areaAssignments, wholeAreaAssignment}) =>
  multipleAreas
    ? areaAssignments.length > 0
    : wholeAreaAssignment[0]?.facilityAssignmentTypeValues?.length > 0;

const transformFirstWholeArea = (firstWholeArea, {assignmentType, checked, id}) => {
  const foundAssignmentType = firstWholeArea.facilityAssignmentTypeValues.find(
    (wholeArea) => wholeArea.assignmentType === assignmentType
  );
  if (!foundAssignmentType) {
    return {
      ...firstWholeArea,
      facilityAssignmentTypeValues: [
        ...firstWholeArea.facilityAssignmentTypeValues,
        {assignmentType, values: [id]}
      ]
    };
  }
  const currentValues = foundAssignmentType.values;
  const values = checked ? [...currentValues, id] : currentValues.filter((value) => value !== id);

  return {
    ...firstWholeArea,
    facilityAssignmentTypeValues: firstWholeArea.facilityAssignmentTypeValues.reduce(
      (acc, wholeArea) => {
        const assignmentTypeAttrs = {
          ...wholeArea,
          values: wholeArea.assignmentType === assignmentType ? values : wholeArea.values
        };

        if (assignmentTypeAttrs.values.length > 0) {
          acc.push(assignmentTypeAttrs);
        }
        return acc;
      },
      []
    )
  };
};

const handleWholeAreaAssignment = (wholeAreaAssignment, assignmentTypeData) => {
  const [firstWholeArea, ...restWholeAreas] = wholeAreaAssignment;
  const firstWholeAreaTransformed = transformFirstWholeArea(firstWholeArea, assignmentTypeData);

  return [firstWholeAreaTransformed, ...restWholeAreas];
};
const getAssignment = ({areas = [], areaId = null, facilityAssignmentTypeValues}) => {
  const area = areas.find(({uuid}) => uuid === areaId);

  if (!area) return;

  return Object.assign(
    {
      uuid: areaId,
      points: area?.latlngs?.map(({lat, lng}) => ({x: lat, y: lng})) ?? [],
      facilityAssignmentTypeValues: facilityAssignmentTypeValues.reduce(
        (acc, {assignmentType, values}) => {
          if (values.length > 0) {
            acc.push({assignmentType, values});
          }
          return acc;
        },
        []
      )
    },
    area.id ? {id: area.id} : null
  );
};

const addAssignment = (areaAssignments, assignmentData) => {
  const assignment = getAssignment(assignmentData);
  const areaFound = areaAssignments.find(({uuid}) => uuid === assignment.uuid);

  if (!areaFound) {
    return [...areaAssignments, assignment];
  }

  return areaAssignments.map((area) => {
    if (area.uuid === areaFound.uuid) {
      return Object.assign(
        {
          ...assignment
        },
        assignment.id ? {status: 'UPDATED'} : null
      );
    }

    return area;
  });
};

const removeAssignment = (areaAssignments, uuid) => {
  const areaFound = areaAssignments.find((area) => area.uuid === uuid);

  if (!areaFound) {
    return areaAssignments;
  }

  if (areaFound && !areaFound.id) {
    return areaAssignments.filter((area) => area.uuid !== uuid);
  }

  return areaAssignments.map((area) =>
    Object.assign(
      {
        ...area
      },
      area.id === areaFound.id ? {delete: true} : null
    )
  );
};

const checkMaxCapacityAvailability = (positions, maxCapacity) => {
  const availableCapacity = positions.reduce((acc, {available}) => {
    if (available) {
      acc = acc + 1;
    }
    return acc;
  }, 0);

  return maxCapacity > availableCapacity;
};

const removeAllAssignments = (multipleAreas, areas) =>
  multipleAreas
    ? areas.map((area) =>
        Object.assign(
          {
            ...area
          },
          area.id ? {delete: true} : null
        )
      )
    : areas.map(({delete: deleted, ...area}, index) =>
        index === 0 ? {...area} : {...area, delete: deleted}
      ); // eslint-disable-line no-unused-vars

const parsePositionsDto = (positions) => {
  return positions
    .filter((p) => {
      if ((p.status === 'DELETE' && !p.id) || (p.status === 'CREATE' && p.id)) {
        return false;
      }

      return true;
    })
    .map((p) => {
      // eslint-disable-next-line no-unused-vars
      const {uuid, error, ...dto} = p;
      return dto;
    });
};

const parseAssignmentsDto = ({multipleAreas, areaAssignments, wholeAreaAssignment}) => {
  const areaAssignmentsFiltered = areaAssignments.filter(
    ({id, delete: deleted, status}) => !id || deleted || status === 'UPDATED'
  );

  const facilityAssignmentsRequest = multipleAreas ? areaAssignmentsFiltered : wholeAreaAssignment;

  if (multipleAreas) {
    if (wholeAreaAssignment[0].id && wholeAreaAssignment[0].delete)
      facilityAssignmentsRequest.push(...wholeAreaAssignment);
  } else {
    const deletedAreas = areaAssignmentsFiltered.reduce((acc, area) => {
      if (area.id && area.delete) {
        acc.push(area);
      }
      return acc;
    }, []);
    facilityAssignmentsRequest.push(...deletedAreas);
  }

  return facilityAssignmentsRequest;
};

const transformFacilityPayloadDto = (floorInfo, floorPlanImage, floorAreas, floorSeats) => ({
  facilityAssignmentsRequest: parseAssignmentsDto(floorAreas),
  facilityBasicInformation: {
    areaSize: floorInfo.areaSize,
    building: floorInfo.building,
    city: floorInfo.city,
    country: floorInfo.country,
    floor: floorInfo.floorName,
    floorMapId: floorPlanImage.name,
    maxCapacity: parseInt(floorInfo.maxCapacity, 10)
  },
  facilityPositionsRequest: parsePositionsDto(floorSeats.positions)
});

const transformFacilityDetailsDto = ({facilityDetails, facilityAssignments, facilityPositions}) => {
  const multipleAreas = Boolean(facilityAssignments[0]?.points);
  const wholeAreaAssignmentDefault = [{facilityAssignmentTypeValues: []}];

  return {
    activeStepNum: 2,
    floorInfo: {
      country: facilityDetails.country,
      city: facilityDetails.city,
      building: facilityDetails.sectorName,
      floorName: facilityDetails.floor,
      areaSize: facilityDetails.areaSize,
      maxCapacity: facilityDetails.maxCapacity
    },
    floorPlanImage: {
      name: facilityDetails.floorMapId,
      url: facilityDetails.url
    },
    floorAreas: {
      multipleAreas,
      areaAssignments: multipleAreas
        ? facilityAssignments.map((assignment) => ({...assignment, uuid: uuid()}))
        : [],
      wholeAreaAssignment: !multipleAreas
        ? facilityAssignments.length === 0
          ? wholeAreaAssignmentDefault
          : facilityAssignments
        : wholeAreaAssignmentDefault
    },
    floorSeats: {
      positions: facilityPositions.map((p) => ({
        ...p,
        uuid: uuid(),
        status: 'CREATE',
        code: p.code == null ? 0 : p.code
      }))
    }
  };
};

export {
  validateFloorInfo,
  validateFloorArea,
  handleWholeAreaAssignment,
  addAssignment,
  removeAssignment,
  removeAllAssignments,
  checkMaxCapacityAvailability,
  transformFacilityPayloadDto,
  transformFacilityDetailsDto
};
